import HyperEntity from "@shared/backend/hyper-entity.decorator";
import { JsonField } from "@shared/helpers/custom-fields";
import { Fields, Relations } from "remult";
import { extender } from "../../backend/class-extender";
import { Association } from "../association";
import { Group } from "../group";
import { Opportunity } from "../opportunity";
import { Organization } from "../organization";
import { OrganizationManager } from "../organization-manager";
import { Team } from "../team";
import { MissionEntityConfig } from "./config";
import { MissionFilters } from "./filters";
import { MissionMethods } from "./methods";

const taskPriorityOptions = ["CRITICAL", "HIGH", "MEDIUM", "LOW"] as const;
export type TaskPriority = (typeof taskPriorityOptions)[number];

const taskStatusOptions = ["DONE", "PENDING", "IN_PROGRESS"] as const;
export type TaskStatus = (typeof taskStatusOptions)[number];

const taskTypeOptions = [
  "GENERAL",
  "USER",
  "AID_RELATED",
  "GROUP",
  "TEAM",
  "OPPORTUNITY",
  "",
] as const;
export type TaskTypes = (typeof taskTypeOptions)[number];

@HyperEntity("mission", MissionEntityConfig)
export class Mission extends extender(MissionFilters, MissionMethods) {
  @Fields.id()
  id = "";

  @Fields.string({
    caption: "תיאור",
  })
  description = "";

  @Fields.date({
    caption: "תאריך ושעה",
  })
  date: Date = new Date();

  @Fields.literal(() => taskPriorityOptions, {
    caption: "דחיפות",
  })
  priority: (typeof taskPriorityOptions)[number] = "MEDIUM";

  @Fields.literal(() => taskTypeOptions)
  type: TaskTypes = "";

  @JsonField<Mission, Mission["types"]>({
    validate: (_, { value }) => {
      if (value === undefined || value === null) return true;
      if (!Array.isArray(value)) return false;
      return value.every((v) =>
        taskTypeOptions.includes(v.toUpperCase() as TaskTypes),
      );
    },
  })
  types?: TaskTypes[] = [];

  @Relations.toOne(() => OrganizationManager, {
    field: "creatorManagerId",
  })
  creatorManager?: OrganizationManager;

  @Fields.string({
    dbName: "creator_manager_id",
  })
  creatorManagerId?: string;

  @Relations.toOne(() => Group, {
    field: "groupId",
  })
  group?: Group;

  @Fields.string({ dbName: "group_id" })
  groupId?: string;

  @Relations.toOne(() => Opportunity, {
    field: "opportunityId",
  })
  opportunity?: Opportunity;

  @Fields.string({ dbName: "opportunity_id" })
  opportunityId?: string;

  @Relations.toOne(() => Organization, {
    field: "organizationId",
  })
  organization?: Organization;

  @Fields.string({ dbName: "organization_id" })
  organizationId?: string;

  @Relations.toOne(() => Team, {
    field: "teamId",
  })
  team?: Team;

  @Fields.string({ dbName: "team_id", allowNull: true })
  teamId?: string | null;

  @Relations.toOne<Mission, Association>(() => Association, {
    fields: {
      id: "associationId",
    },
    dbName: "association_id",
  })
  association?: Association;

  @Fields.string({ dbName: "association_id" })
  associationId?: string;

  @Relations.toOne(() => OrganizationManager, {
    field: "assignedManagerId",
  })
  assignedManager?: OrganizationManager;

  @Fields.string({
    dbName: "assigned_manager_id",
  })
  assignedManagerId?: string;

  @Fields.literal(() => taskStatusOptions, {
    caption: "סטטוס",
  })
  status: TaskStatus = "PENDING";

  @Fields.string({
    caption: "הערות",
  })
  comments?: string = "";

  @Fields.boolean({
    caption: "בארכיון",
    dbName: "is_archived",
    defaultValue: () => false,
  })
  isArchived = false;

  @Fields.integer()
  order = 0;

  @Fields.updatedAt({ dbName: "updated_at" })
  updatedAt: Date = new Date();

  @Fields.createdAt({ dbName: "created_at" })
  createdAt: Date = new Date();
}
